import { getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js'

/**
 * Country handling for patient mobile numbers.
 *
 * A front-desk user often types the local number without a country code. The clinic's own
 * country decides which code to assume (India when nothing better is known).
 */

export const DEFAULT_PHONE_COUNTRY_ISO = 'IN'

function cleanIso(value) {
  if (typeof value !== 'string') return ''
  const iso = value.trim().toUpperCase()
  return /^[A-Z]{2}$/.test(iso) ? iso : ''
}

/** ISO-2 country of a clinic record, trying every shape the clinic payloads have used. */
export function getClinicCountryIso(clinicData) {
  const candidates = [
    clinicData?.location?.countryCode,
    clinicData?.countryCode,
    clinicData?.address?.countryCode,
    clinicData?.clinicBrandData?.location?.countryCode,
    clinicData?.timeZone?.countryCode,
  ]
  for (const candidate of candidates) {
    const iso = cleanIso(candidate)
    if (iso) return iso
  }
  return ''
}

/** Clinic country → remembered device country → India. */
export function resolveDefaultPhoneCountryIso({ clinicData, fallbackIso } = {}) {
  const fromClinic = getClinicCountryIso(clinicData)
  if (fromClinic) return fromClinic
  const explicit = cleanIso(fallbackIso)
  if (explicit) return explicit
  try {
    const stored = cleanIso(globalThis?.localStorage?.getItem?.('countryCode'))
    if (stored) return stored
  } catch (error) {
    // storage unavailable
  }
  return DEFAULT_PHONE_COUNTRY_ISO
}

export function getDialCodeForIso(iso) {
  try {
    return getCountryCallingCode(cleanIso(iso) || DEFAULT_PHONE_COUNTRY_ISO)
  } catch (error) {
    return '91'
  }
}

/**
 * Digits-only mobile (as stored by the phone input, no "+") with a country code guaranteed.
 * - already international and valid → unchanged
 * - a national number for `iso` → prefixed with that country's dial code
 * - anything else → returned as typed (validation will flag it)
 */
export function ensureMobileHasCountryCode(mobile, iso = DEFAULT_PHONE_COUNTRY_ISO) {
  if (typeof mobile !== 'string') return mobile
  const digits = mobile.replace(/[^\d]/g, '')
  if (!digits) return ''
  const country = cleanIso(iso) || DEFAULT_PHONE_COUNTRY_ISO

  const international = parsePhoneNumberFromString(`+${digits}`)
  if (international?.isValid()) {
    return international.number.replace(/^\+/, '')
  }

  const national = parsePhoneNumberFromString(digits, country)
  if (national?.isValid()) {
    return national.number.replace(/^\+/, '')
  }

  return digits
}
